import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

export type AuthAnimationType = 'brandFadeIn' | null;

export interface AuthAnimationState {
  active: boolean;
  type: AuthAnimationType;
  duration: number;
  playId: number;
}

@Injectable({
  providedIn: 'root',
})
export class AuthAnimationService implements OnDestroy {
  private readonly stateSubject = new BehaviorSubject<AuthAnimationState>({
    active: false,
    type: null,
    duration: 0,
    playId: 0,
  });
  private activeTimeout: ReturnType<typeof setTimeout> | null = null;
  private dashboardRevealQueued = false;
  private readonly dashboardRevealSubject = new Subject<void>();

  readonly state$ = this.stateSubject.asObservable();
  readonly dashboardReveal$ = this.dashboardRevealSubject.asObservable();

  playBrandFadeIn(duration = 10000, holdAfterComplete = 0): Promise<void> {
    this.clearActiveTimeout();

    const nextPlayId = this.stateSubject.value.playId + 1;
    const totalRunTime = duration + Math.max(0, holdAfterComplete);

    this.stateSubject.next({
      active: true,
      type: 'brandFadeIn',
      duration,
      playId: nextPlayId,
    });

    return new Promise((resolve) => {
      this.activeTimeout = setTimeout(() => {
        this.stateSubject.next({
          active: false,
          type: null,
          duration: 0,
          playId: nextPlayId,
        });
        this.clearActiveTimeout();
        resolve();
      }, totalRunTime);
    });
  }

  queueDashboardReveal(): void {
    this.dashboardRevealQueued = true;
  }

  /**
   * Whether registration queued the first-login reveal. The exits to the
   * dashboard are shared with password login, so they check this before
   * playing the brand fade.
   */
  isDashboardRevealQueued(): boolean {
    return this.dashboardRevealQueued;
  }

  emitDashboardRevealIfQueued(): void {
    if (!this.dashboardRevealQueued) {
      return;
    }
    this.dashboardRevealQueued = false;
    this.dashboardRevealSubject.next();
  }

  ngOnDestroy(): void {
    this.clearActiveTimeout();
  }

  private clearActiveTimeout() {
    if (this.activeTimeout) {
      clearTimeout(this.activeTimeout);
      this.activeTimeout = null;
    }
  }

  async playBrandFadeInAndRevealDashboardIfQueued() {
    await this.playBrandFadeIn();
    this.emitDashboardRevealIfQueued();
  }

  isBrandAnimationActive(): boolean {
    return this.stateSubject.value.active;
  }
}
